import axios from "axios";
import { backendUrl } from "../../api/constants";
import _ from "lodash";
import {v4 as uuidv4} from 'uuid';

export const localStorageSessionKey = "analyticsSession";
export const analyticsSendInterval = 30000 // 30 seconds 
export const sessionTimeoutThreshold = 1800000 // 30 minutes 
export const maxSessionLength = 86400000; // 24 hours

let previousSession = null;

const getLocation = () =>
  new Promise((resolve, reject) => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
        },
        () => {
          resolve(null); // resolve with null in case of error or denial of permission
        }
      );
    } else {
      resolve(null); // resolve with null if geolocation is not supported
    }
  });

export const startNewSession = async (user) => {
  const location = await getLocation();
  const session = {
    id: uuidv4(),
    sessionStart: Date.now(),
    lastActivity: Date.now(),
    userAgent: navigator.userAgent,
    user: user?._id,
    referrer: document.referrer,
    events: [],
    location,
  };

  localStorage.setItem(localStorageSessionKey, JSON.stringify(session));

  console.log('🎬 [ANALYTICS] NEW SESSION STARTED:', {
    sessionId: session.id,
    userId: user?._id || 'anonymous',
    location: location ? `${location.latitude}, ${location.longitude}` : 'no location',
    timestamp: new Date(session.sessionStart).toLocaleTimeString()
  });

  return session;
};

export const sendSessionData = async () => {
  const session = JSON.parse(localStorage.getItem(localStorageSessionKey));

  if (!session) {
    console.log('⚠️ [ANALYTICS] No session found, cannot send data');
    return;
  }

  if(import.meta.env.VITE_DISABLE_ANALYTICS === 'true') {
    console.log('🚫 [ANALYTICS] Analytics disabled by environment variable');
    return;
  }

  // if session hasn't changed, don't send it
  const areSessionsEqual = _.isEqualWith(previousSession, session, (value, other, key) => {
    if (key === 'lastActivity') return true;
  });

  if (areSessionsEqual) {
    console.log('⏭️ [ANALYTICS] Session unchanged, skipping send');
    return;
  }

  console.log('📤 [ANALYTICS] SENDING SESSION DATA:', {
    sessionId: session.id,
    userId: session.user || 'anonymous',
    eventCount: session.events.length,
    events: session.events.map(e => `${e.action} (${e.category})`).join(', ') || 'none',
    endpoint: `${backendUrl}/analytics/session`,
    timestamp: new Date().toLocaleTimeString()
  });

  try {
    // Send analytics data with timeout to prevent hanging requests
    const response = await axios.post(`${backendUrl}/analytics/session`, session, {
      timeout: 10000, // 10 second timeout
    });

    console.log('✅ [ANALYTICS] SESSION DATA SENT SUCCESSFULLY:', {
      sessionId: session.id,
      eventsCleared: session.events.length,
      responseStatus: response.status,
      timestamp: new Date().toLocaleTimeString()
    });

    // Only clear events array if request succeeded
    session.events = [];
    previousSession = session;
    localStorage.setItem(localStorageSessionKey, JSON.stringify(session));
    
  } catch (error) {
    // Log the error for debugging but don't throw - analytics shouldn't break the app
    console.error('❌ [ANALYTICS] SEND FAILED:', {
      sessionId: session.id,
      eventCount: session.events.length,
      error: error.message,
      errorType: error.name,
      status: error.response?.status,
      timestamp: new Date().toLocaleTimeString()
    });
    console.warn('🔄 [ANALYTICS] Events will be retried on next heartbeat (30s)');
    
    // DON'T clear the events array - keep them for the next attempt
    // This way, events accumulate until successfully sent
    // The events will be sent again in the next heartbeat cycle (30s)
  }
};

export const updateSession = async (newSession) => {
  const session = JSON.parse(localStorage.getItem(localStorageSessionKey));

  if (!session) return;

  const updatedSession = {
    ...session,
    ...newSession,
    lastActivity: Date.now(),
  };

  localStorage.setItem(localStorageSessionKey, JSON.stringify(updatedSession));

  return updatedSession;
};

export const sendHeartbeat = (currentUser) => {
  // if lastActivity is more than 5 seconds ago, send a heartbeat
  const session = JSON.parse(localStorage.getItem(localStorageSessionKey));

  if (!session) return;

  const sessionAge = Date.now() - session.lastActivity;

  if (sessionAge >= analyticsSendInterval) {
    // console log sessionAge converted to seconds
    heartBeat(currentUser);
  }
};

export const heartBeat = async (currentUser) => {
  const session = await updateSession({
    user: currentUser?._id,
  });

  if (!session) {
    console.log('⚠️ [ANALYTICS] Heartbeat: No session found');
    return;
  }

  const sessionAge = Date.now() - session.lastActivity;
  const sessionDuration = Date.now() - session.sessionStart;

  console.log('💓 [ANALYTICS] HEARTBEAT:', {
    sessionId: session.id,
    userId: session.user || 'anonymous',
    sessionAge: `${Math.floor(sessionAge / 1000)}s`,
    sessionDuration: `${Math.floor(sessionDuration / 60000)}min`,
    eventsPending: session.events.length,
    timestamp: new Date().toLocaleTimeString()
  });

  if (sessionAge > sessionTimeoutThreshold || Date.now() - session.sessionStart > maxSessionLength) {
    // session is too old, start a new one
    console.log('🔄 [ANALYTICS] Session timeout/expired, starting new session');
    await sendSessionData();
    await startNewSession(currentUser); // Pass currentUser to maintain proper user attribution
  } else {
    await sendSessionData();
  }
};


// ************************************************************************************************
// * EVENT TRACKING
// ************************************************************************************************

/**
 * Push a granular engagement event into the pending session payload.
 * Example ticket click payload:
 * {
 *   action: "event ticket clicked",
 *   category: "event",
 *   label: "event_ticket_clicked",
 *   event: "<eventId>",
 *   value: { name, address, category, source: "event_details_ticket_button", ticketLink },
 *   timestamp: Date.now()
 * }
 */
export const trackSessionEvent = async (event) => {
    const session = JSON.parse(localStorage.getItem(localStorageSessionKey));
    
    if (!session) {
      console.warn('⚠️ [ANALYTICS] Cannot track event - no session exists:', event);
      return;
    }
    
    const newSession = {
        ...session,
        events: [...session.events, event]
    }

    console.log('📝 [ANALYTICS] EVENT TRACKED:', {
      action: event.action,
      category: event.category,
      label: event.label,
      eventId: event.event || event.venue || 'n/a',
      totalEvents: newSession.events.length,
      sessionId: session.id,
      timestamp: new Date(event.timestamp).toLocaleTimeString()
    });

    localStorage.setItem(localStorageSessionKey, JSON.stringify(newSession));
}

// ************************************************************************************************
